import DataCards from '../../api/data_cards';
const dataCards = new DataCards();

/**
 * @typedef {Object} GroupCard
 * @property {number} id
 * @property {string} name
 * @property {string} [description]
 * @property {number|null} organization_id
 * @property {number|null} created_by
 * @property {string} [status]
 * @property {Array} [usersGroupCards]
 * @property {Array} [topicsGroupCards]
 * @property {Array} [subTopicsCards]
 */

function normalizeGroupCard(card) {
    if (!card || typeof card !== 'object') {
        return card;
    }
    return {
        ...card,
        organization_id: card.organization_id != null && card.organization_id !== ''
            ? Number(card.organization_id)
            : null,
        created_by: card.created_by != null && card.created_by !== ''
            ? Number(card.created_by)
            : null,
    };
}

function extractGroupCard(response) {
    if (!response) {
        return null;
    }
    const card = response.groupCard || response.group_card || response;
    if (!card || typeof card !== 'object' || Array.isArray(card)) {
        return null;
    }
    if (card.id == null && card.organization_id == null && !card.name) {
        return null;
    }
    return normalizeGroupCard(card);
}

const dataCardsModule = {
    namespaced: true,
    state: {
        loading: false,
        dataCards: [],
        totalDataCards: 0,
        groupMembers: [],
        subTopicsCards: [],
        totalGroupMembers: 0,
        totalSubTopicsCards: 0,
        groupName: null,
        currentGroupCard: null,
        institutionInstructors: [],
        groupCardLookups: [],
    },
    getters: {
        dataCards: (state) => state.dataCards,
        totalDataCards: (state) => state.totalDataCards,
        groupMembers: (state) => state.groupMembers,
        subTopicsCards: (state) => state.subTopicsCards,
        loading: (state) => state.loading,
        totalGroupMembers: (state) => state.totalGroupMembers,
        totalSubTopicsCards: (state) => state.totalSubTopicsCards,
        groupName: (state) => state.groupName,
        currentGroupCard: (state) => state.currentGroupCard,
        institutionInstructors: (state) => state.institutionInstructors,
        groupCardLookups: (state) => state.groupCardLookups,
    },
    mutations: {
        SET_LOADING: (state, loading) => {
            state.loading = loading;
        },
        SET_DATA_CARDS: (state, data) => {
            state.dataCards = Array.isArray(data) ? data.map(normalizeGroupCard) : [];
        },
        SET_TOTAL_DATA_CARDS: (state, count) => {
            state.totalDataCards = count;
        },
        SET_GROUP_MEMBERS: (state, data) => {
            state.groupMembers = data;
        },
        SET_TOTAL_GROUP_MEMBERS: (state, totalGroupMembers) => {
            state.totalGroupMembers = totalGroupMembers;
        },
        SET_SUB_TOPICS_CARDS: (state, data) => {
            state.subTopicsCards = data;
        },
        SET_TOTAL_SUB_TOPICS_CARDS: (state, count) => {
            state.totalSubTopicsCards = count;
        },
        SET_GROUP_NAME: (state, groupName) => {
            state.groupName = groupName;
        },
        SET_CURRENT_GROUP_CARD: (state, groupCard) => {
            state.currentGroupCard = groupCard ? normalizeGroupCard(groupCard) : null;
        },
        SET_INSTITUTION_INSTRUCTORS: (state, instructors) => {
            state.institutionInstructors = instructors || [];
        },
        SET_GROUP_CARD_LOOKUPS: (state, lookups) => {
            state.groupCardLookups = Array.isArray(lookups) ? lookups.map(normalizeGroupCard) : [];
        },
    },
    actions: {
        GetDataCards({ commit, state }, payload) {
            const query = (payload && payload.query) || {};
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.list(query).then(response => {
                    if (response) {
                        const items = response.groupCards || response.group_cards || [];
                        let incomingCount = response.total || response.count || 0;
                        const offset = query.offset || 0;

                        // Heuristic to prevent incorrect total from API triggering pagination reset
                        if (offset > 0 && items.length > 0 && incomingCount < (offset + items.length)) {
                            incomingCount = state.totalDataCards;
                        }
                        commit('SET_DATA_CARDS', items);
                        commit('SET_TOTAL_DATA_CARDS', incomingCount);
                    }

                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        GetGroupMembers({ commit }, payload) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.usersGroupCards(payload.id, payload.query).then(response => {
                    if (response) {
                        commit('SET_GROUP_MEMBERS', (response && response.usersGroupCards) || []);
                        commit('SET_GROUP_NAME', (response && response.group_name) || []);
                        commit('SET_TOTAL_GROUP_MEMBERS', (response && response.total) || 0);
                        // commit('SET_SUB_TOPICS_CARDS', (response.groupCard && response.groupCard.subTopicsCards) || []);
                    }
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        GetSubTopicsCards({ commit }, payload) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.subTopicsCards(payload.id, payload.query).then(response => {
                    commit('SET_SUB_TOPICS_CARDS', (response && response.subTopicsGroupCards) || []);
                    commit('SET_TOTAL_SUB_TOPICS_CARDS', (response && response.total) || 0);
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        UpdateGroup({ commit }, payload) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.update(payload.id, payload.data).then(response => {
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        ShowGroupCard({ commit }, id) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.show(id).then(response => {
                    const card = extractGroupCard(response);
                    if (card) {
                        commit('SET_CURRENT_GROUP_CARD', card);
                        if (card.name) {
                            commit('SET_GROUP_NAME', card.name);
                        }
                    }
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        GetGroupCardLookups({ commit }, payload) {
            const query = (payload && payload.query) || payload || {};
            return new Promise((resolve, reject) => {
                dataCards.lookups(query).then(response => {
                    const items = (response && (response.groupCards || response.group_cards || response.lookups)) || [];
                    commit('SET_GROUP_CARD_LOOKUPS', Array.isArray(items) ? items : []);
                    resolve(response);
                }).catch(error => {
                    reject(error);
                });
            });
        },
        GetInstitutionInstructors({ commit }, payload) {
            const query = (payload && payload.query) || {};
            const groupCardId = payload && payload.groupCardId;
            return new Promise((resolve, reject) => {
                dataCards.institutionInstructors(query, groupCardId).then(response => {
                    commit('SET_INSTITUTION_INSTRUCTORS', (response && response.institution_users) || []);
                    resolve(response);
                }).catch(error => {
                    commit('SET_INSTITUTION_INSTRUCTORS', []);
                    reject(error);
                });
            });
        },
        AddGroupMembers({ commit }, payload) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                const body = {
                    users: payload.userIds || payload.users || [],
                    ...(payload.organization_id ? { organization_id: payload.organization_id } : {})
                };
                dataCards.addUsers(payload.id, body).then(response => {
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        RemoveGroupMember({ commit }, payload) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.removeMember(payload.id, payload.userId).then(response => {
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        CreateDataCard({ commit }, payload) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.store(payload).then(response => {
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        DeleteDataCard({ commit }, id) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.delete(id).then(response => {
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        UnAssignMe({ commit }, groupId) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.unAssignMe(groupId).then(response => {
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        SubmitStudentAnswers({ commit }, payload) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.submitAnswers(payload).then(response => {
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
        UpdateMemberStatus({ commit, state }, payload) {
            return new Promise((resolve, reject) => {
                commit('SET_LOADING', true);
                dataCards.updateMemberStatus(payload.groupId, payload.userId, payload.status).then(response => {
                    const resolvedStatus = (response && response.status) || payload.status;
                    const updatedMembers = state.groupMembers.map(m => {
                        if (m.id === payload.userId || m.user_id === payload.userId) {
                            return { ...m, status: resolvedStatus };
                        }
                        return m;
                    });
                    commit('SET_GROUP_MEMBERS', updatedMembers);
                    commit('SET_LOADING', false);
                    resolve(response);
                }).catch(error => {
                    commit('SET_LOADING', false);
                    reject(error);
                });
            });
        },
    }
};

export default dataCardsModule;
